import React, { useState, useEffect } from 'react';
import { useToggle } from 'react-use';

import { User } from 'firebase/auth';
import { Box, Drawer, IconButton, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import { NavbarButton } from './NavbarButton';
import { CompanyReferralDropdown } from './CompanyReferralDropdown';
import { UserDropdown } from './UserDropdown';

import { getNotificationCountByUser } from 'apis';

interface NavbarMenuProps {
  user?: User;
  isReferrer: boolean;
  isAdmin: boolean;
}

const getLinks = (isReferrer: boolean, isAdmin: boolean, user?: User) => {
  const referralLinks = [
    { href: '/companies', children: '內推公司列表' },
    { href: '/signup?type=referrer', children: '成為推薦人' },
    { href: user ? '/applications/sent' : '/signin', children: '別人推我' },
    { href: user ? '/applications' : '/signin', children: '我推別人' },
  ];

  if (!user) {
    return [
      { children: '公司內推', subItems: referralLinks },
      { href: '/door', children: '預約諮詢' },
      { href: '/signin', children: '訊息' },
      { href: '/signin', children: '登入' },
    ];
  }

  const userLinks = [
    { href: `/profile/${user?.uid}`, children: '個人資料' },
    { href: '/edit/profile', children: '編輯個人資料' },
    { href: '/signout', children: '登出' },
  ];

  const links = [
    { children: '公司內推', subItems: referralLinks },
    { href: '/door', children: '預約諮詢' },
    { href: '/messages', children: '訊息' },
  ];

  if (isAdmin) {
    links.push({ href: '/admin', children: '後台' });
  }

  links.push({ children: 'UserDropdown', subItems: userLinks });

  return links;
};



export const NavbarMenu = ({ user, isReferrer, isAdmin }: NavbarMenuProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useToggle(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    if (isDesktop && isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  }, [isDesktop, isDrawerOpen, setIsDrawerOpen]);

  const [notificationCount, setNotificationCount] = useState(0);
  useEffect(() => {
    const fetchUnreadMessagesCount = async () => {
      if (user) {
        await getNotificationCountByUser(user.uid, setNotificationCount);
      }
    };
    fetchUnreadMessagesCount();
  }, [user]);

  const handleMenuToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const nCount = () => {
    if (notificationCount !== 0) {
      return notificationCount;
    }
    return null;
  };

  const links = getLinks(isReferrer, isAdmin, user);

  return (
    <>
      {/* mobile */}
      <IconButton
        edge="end"
        aria-label="menu"
        onClick={handleMenuToggle}
        size="large"
        sx={{
          display: { xs: 'flex', md: 'none' },
          padding: '4px',
          marginRight: '-4px',
        }}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        anchor="top"
        open={isDrawerOpen}
        onClose={handleMenuToggle}
        BackdropProps={{ invisible: true }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '4px',
            margin: '60px 4px 4px 4px',
          }}
        >
          {links.map((link) => {
            if (link.subItems) {
              return link.subItems.map((subLink: any) => (
                <NavbarButton
                  href={subLink.href}
                  key={`navbar-button-mobile-${subLink.children}`}
                >
                  {subLink.children}
                </NavbarButton>
              ));
            }
            return (
              <NavbarButton
                href={link.href}
                key={`navbar-button-mobile-${link.children}`}
                notificationCount={link.children === '訊息' ? nCount() : null}
              >
                {link.children}
              </NavbarButton>
            );
          })}

        </Box>
      </Drawer>

      {/* desktop */}
      <Box
        component="nav"
        sx={{ display: { xs: 'none', md: 'flex' }, columnGap: '8px' }}
      >
{links.map(({ href, children }) => {
          if (children === '公司內推') {
            return (
              <CompanyReferralDropdown 
                key={`navbar-dropdown-${children}`} 
                isLoggedIn={!!user}
                isReferrer={isReferrer}
              />
            );
          }
          if (children === 'UserDropdown' && user) {
            return (
              <UserDropdown key="navbar-user-dropdown" user={user} />
            );
          }
          return (
            <NavbarButton
              href={href}
              key={`navbar-button-${children}`}
              notificationCount={children === '訊息' ? nCount() : null}
            >
              {children}
            </NavbarButton>
          );
        })}

      </Box>
    </>
  );
};
